import {
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from '@mui/material';
import React, { useMemo, useState } from 'react';

const MultiStepForm = ({
  notificationType,
  onSubmit,
  availableAtSource,
  incorrectAvailableAtSourceFlag,
  notificationCanbeSentFlag,
  curatorNotes
}: {
  notificationType: string;
  onSubmit: any;
  availableAtSource: boolean;
  incorrectAvailableAtSourceFlag: boolean;
  notificationCanbeSentFlag: boolean;
  curatorNotes: string;
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [incorrectAvailableAtSource, setIncorrectAvailableAtSource] = useState<boolean>(
    incorrectAvailableAtSourceFlag
  );
  const [changeType, setChangeType] = useState<string>(notificationType);
  const [notificationCanBeSent, setNotificationCanBeSent] =
    useState<boolean>(notificationCanbeSentFlag);
  const [qcNotes, setQCNotes] = useState<string>(curatorNotes);

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleSubmit = () => {
    const updatedData = {
      notificationCanBeSent,
      qcNotes,
      notificationType,
      incorrectAvailableAtSource
    };
    onSubmit?.(updatedData);
  };

  const steps = useMemo(
    () => [
      {
        fullText: `Currently our system has marked it as ${
          availableAtSource ? 'available at source' : 'not available at source'
        }. Is it correct?`,
        shortText: 'Correct system status?'
      },
      {
        fullText: `Our system has marked it as ${notificationType}. What is the status as per your validations?`,
        shortText: 'Change Type'
      },
      { fullText: 'Notification can be sent?', shortText: 'Notification can be sent?' },
      { fullText: 'QC Notes', shortText: 'QC Notes' }
    ],
    [availableAtSource]
  );
  const renderQuestion = () => {
    switch (activeStep) {
      case 0:
        return (
          <RadioGroup
            value={!incorrectAvailableAtSource}
            onChange={e => setIncorrectAvailableAtSource(e.target.value !== 'true')}>
            <FormControlLabel value='true' control={<Radio />} label='Correct' />
            <FormControlLabel value='false' control={<Radio />} label='Incorrect' />
          </RadioGroup>
        );
      case 1:
        return (
          <RadioGroup value={changeType} onChange={e => setChangeType(e.target.value)}>
            <FormControlLabel value='Deleted' control={<Radio />} label='Deleted' />
            <FormControlLabel value='ReAppeared' control={<Radio />} label='ReAppeared' />
          </RadioGroup>
        );
      case 2:
        return (
          <RadioGroup
            value={notificationCanBeSent}
            onChange={e => setNotificationCanBeSent(e.target.value === 'true')}>
            <FormControlLabel value='true' control={<Radio />} label='Yes' />
            <FormControlLabel value='false' control={<Radio />} label='No' />
          </RadioGroup>
        );
      case 3:
        return (
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder='Title with issue date is available in the main list. Link from title opens webpage without any error messages. Web page does not have the pdf link. So it is not available at source.'
            value={qcNotes}
            onChange={e => setQCNotes(e.target.value)}
          />
        );
      default:
        return <Typography>Review your answers before submission.</Typography>;
    }
  };

  return (
    <Paper sx={{ p: 4, w: '100%' }}>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((step, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Step key={index}>
            <StepLabel>{step.shortText}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ m: 'auto', mt: 4 }}>
        <Typography>{steps[activeStep].fullText}</Typography>
        {renderQuestion()}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack}>
          Back
        </Button>
        {activeStep < steps.length - 1 ? (
          <Button variant='contained' onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button variant='contained' color='success' onClick={handleSubmit}>
            Submit
          </Button>
        )}
      </Box>
    </Paper>
  );
};

export default React.memo(MultiStepForm);
